import _, { get, set, isArray, map } from 'lodash';
import type { FSA } from 'types/redux';
import {
  GET_REWARD_STATUS,
  GET_TEMPLATE,
  GET_TEMPLATE_LIST,
  GET_TIME_PROGRESS,
  GET_LIST_RULE_TYPE,
  GET_CAMPAIGN_BUDGET_SOURCES,
  GET_CAMPAIGN_TARGET_VERTICALS,
  GET_AWARD_POINT_ACTION,
  SEARCH_CAMPAIGN_HISTORY,
  GET_FILTER_TYPES,
  GET_APPROVERS,
  GET_DYNAMIC_INPUT_TYPES,
  GET_CAMPAIGN_METRICS,
  GET_CAMPAIGN_NOT_SENT_METRICS,
  GET_ECA_CONFIG_BY_KEY,
  GET_SEGP_CONFIG,
  GET_APPROVAL_FLOW_CONFIG,
  GET_FEATURE_FLAG_CONFIG,
  CAMPAIGN_APPROVAL_FLOW,
  CHANGE_STATUS,
  FETCH_PARQUET_USER_ATTRIBUTES,
  SEARCH_CAMPAIGNS_FULL,
} from 'actions/newTridentExtra';
import { TemplateInfoMap } from 'types/trident-campaigns';
import * as RequestResultCases from 'middlewares/api-middleware';
import { CREATE_ENTITY, GET_ENTITY, EDIT_ENTITY } from 'omega-rtc/actions/trident-campaigns';
import { CampaignBudgetSources, CampaignTargetVerticals } from 'omega-rtc/data/trident-campaigns';

export type TridentState = {
  templates: TemplateInfoMap,
  rewardStatus: any,
  progress: any,
  listRuleTypes: any,
  parquetUserAttributesPreview: any,
};

export const defaultTridentState = {
  templates: {},
  rewardStatus: {},
  progress: {},
  listRuleTypes: {},
  eventConditionActionConfigs: {},
  parquetUserAttributesPreview: {},
};

const newtrident = (
  state: TridentState = defaultTridentState as TridentState,
  action: FSA,
): TridentState => {
  switch (action.type) {
    case CREATE_ENTITY + RequestResultCases.SUCCESS:
      return {
        ...state,
        newlyCreatedID: action.payload.ID,
        ucdErrMessage: get(action, 'payload.ucdErrMessage'),
      };
    case EDIT_ENTITY + RequestResultCases.SUCCESS:
      return {
        ...state,
        ucdErrMessage: get(action, 'payload.ucdErrMessage'),
      };
    case CAMPAIGN_APPROVAL_FLOW + RequestResultCases.SUCCESS:
      return {
        ...state,
        approvalRequest: {
          actionType: get(action, 'meta.request.data.actionType'),
        },
        ucdErrMessage: get(action, 'payload.ucdErrMessage'),
      };
    case CHANGE_STATUS + RequestResultCases.SUCCESS:
      return {
        ...state,
        ucdErrMessage: get(action, 'payload.ucdErrMessage'),
      };
    case GET_ENTITY + RequestResultCases.SUCCESS:
      return {
        ...state,
        entities: action.payload,
      };
    case GET_TEMPLATE + RequestResultCases.SUCCESS: {
      const templates = get(state, 'templates', {});
      const templateID = get(action, 'meta.templateID');

      const payload = {
        ...action.payload[0],
        ...get(action, 'payload[1].template', {}),
      };
      set(templates, `${templateID}`, payload);

      return {
        ...state,
        templates,
      };
    }
    case GET_TEMPLATE + RequestResultCases.FAILURE: {
      const errorMessage = get(action, 'payload.response.data.message');
      return {
        ...state,
        templates: { errorMessage },
      };
    }
    case GET_TEMPLATE_LIST + RequestResultCases.SUCCESS: {
      const templateMap = get(state, 'templateMap', {}); // a map: templateID -> template object

      map(action.payload, item => {
        set(templateMap, get(item, 'templateId'), get(item, 'template', {}));
      });

      return {
        ...state,
        templateMap,
      };
    }
    case GET_REWARD_STATUS + RequestResultCases.SUCCESS:
      return {
        ...state,
        rewardStatus: {
          ...action.payload,
          rewardID: get(action, 'meta.rewardID'),
        },
      };
    case GET_TIME_PROGRESS + RequestResultCases.SUCCESS: {
      const progress = get(state, 'progress');
      progress[get(action, 'meta.currentNodeProgressKey')] = action.payload;
      return {
        ...state,
        progress,
      };
    }
    case FETCH_PARQUET_USER_ATTRIBUTES + RequestResultCases.REQUEST: {
      const previewKey = get(action, 'meta.previewKey', 'default');
      return {
        ...state,
        parquetUserAttributesPreview: {
          ...get(state, 'parquetUserAttributesPreview', {}),
          [previewKey]: {
            fetchResponse: null,
            fetchError: null,
          },
        },
      };
    }
    case FETCH_PARQUET_USER_ATTRIBUTES + RequestResultCases.SUCCESS: {
      const previewKey = get(action, 'meta.previewKey', 'default');
      return {
        ...state,
        parquetUserAttributesPreview: {
          ...get(state, 'parquetUserAttributesPreview', {}),
          [previewKey]: {
            ...get(state, `parquetUserAttributesPreview.${previewKey}`, {}),
            fetchResponse: action.payload,
            fetchError: null,
          },
        },
      };
    }
    case FETCH_PARQUET_USER_ATTRIBUTES + RequestResultCases.FAILURE: {
      const previewKey = get(action, 'meta.previewKey', 'default');
      return {
        ...state,
        parquetUserAttributesPreview: {
          ...get(state, 'parquetUserAttributesPreview', {}),
          [previewKey]: {
            ...get(state, `parquetUserAttributesPreview.${previewKey}`, {}),
            fetchResponse: null,
            fetchError: get(action, 'payload.response.data.message', 'Failed to load user attributes'),
          },
        },
      };
    }
    case GET_LIST_RULE_TYPE + RequestResultCases.SUCCESS: {
      let ruleTypesObj = {};
      const listEventConditionActionResponse = _.get(action, 'payload.listEventConditionActionResponse');
      if (isArray(listEventConditionActionResponse)) {
        // in the new listRuleType response, the payload is an array
        // to make it compatible with existing logic, we wrap it in an object
        const targetUserType = get(
          action,
          'meta.request.data.listEventConditionActionRequest.targetUserType',
        );
        set(ruleTypesObj, targetUserType, listEventConditionActionResponse);
      } else {
        // TODO: this is handling deprecated response, to remove after migration
        ruleTypesObj = listEventConditionActionResponse;
      }
      return {
        ...state,
        listRuleTypes: ruleTypesObj,
      };
    }
    case GET_DYNAMIC_INPUT_TYPES + RequestResultCases.SUCCESS: {
      return {
        ...state,
        dynamicInputTypes: _.get(action, 'payload.dynamicInputTypeResponse'),
      };
    }
    case GET_ECA_CONFIG_BY_KEY + RequestResultCases.SUCCESS: {
      const key = get(action, 'meta.ecaKey');
      const next = { ...state.eventConditionActionConfigs };
      // Extract the actual config data from GetConfigurationsResponse structure
      const configData = get(action, 'payload.eventConditionActionConfigResponse');
      next[key] = configData || action.payload; // Fallback to action.payload for backward compatibility
      return { ...state, eventConditionActionConfigs: next };
    }
    case GET_CAMPAIGN_BUDGET_SOURCES: {
      // const budgetSources = get(action.payload, 'listBudgetSourceResponse', []);
      return {
        ...state,
        campaignBudgetSources: _.map(CampaignBudgetSources, (text, value) => {
          return { text, value };
        }),
      };
    }

    case GET_CAMPAIGN_TARGET_VERTICALS: {
      // const targetVerticals = get(action.payload, 'listTargetVerticalResponse', []);
      return {
        ...state,
        campaignTargetVerticals: _.map(CampaignTargetVerticals, (text, value) => {
          return { text, value };
        }),
      };
    }
    case GET_AWARD_POINT_ACTION + RequestResultCases.SUCCESS: {
      const budgetSource = get(
        action.payload,
        'getAwardPointActionResponse.budgetSource',
        [],
      );
      const purpose = get(
        action.payload,
        'getAwardPointActionResponse.purpose',
        [],
      );
      return {
        ...state,
        awardPointAction: {
          budgetSource,
          purpose,
        },
      };
    }
    case SEARCH_CAMPAIGN_HISTORY + RequestResultCases.SUCCESS: {
      return {
        ...state,
        campaignHistory: get(action.payload, 'items', []),
      };
    }
    case GET_FILTER_TYPES + RequestResultCases.SUCCESS: {
      return {
        ...state,
        filterTypes: get(action.payload, 'getAdvancedFilterResponse', {}),
      };
    }
    case GET_APPROVERS + RequestResultCases.SUCCESS: {
      return {
        ...state,
        approvers: action.payload,
      };
    }
    case GET_CAMPAIGN_METRICS + RequestResultCases.SUCCESS: {
      return {
        ...state,
        campaignMetrics: action.payload,
      };
    }
    case GET_CAMPAIGN_NOT_SENT_METRICS + RequestResultCases.SUCCESS: {
      return {
        ...state,
        campaignNotSentMetrics: action.payload,
      };
    }
    case GET_SEGP_CONFIG + RequestResultCases.SUCCESS: {
      return {
        ...state,
        segPConfig: get(action.payload, 'getSegPWidgetConfigResponse', {}),
      };
    }
    case GET_FEATURE_FLAG_CONFIG + RequestResultCases.SUCCESS: {
      return {
        ...state,
        featureFlagConfig: get(action.payload, 'featureFlagConfigResponse', {}),
      };
    }
    case GET_APPROVAL_FLOW_CONFIG + RequestResultCases.SUCCESS: {
      return {
        ...state,
        approvalFlowConfig: get(
          action.payload,
          'approvalFlowConfigResponse',
          {},
        ),
      };
    }
    case SEARCH_CAMPAIGNS_FULL + RequestResultCases.SUCCESS: {
      return {
        ...state,
        fullCampaignSearchResults: get(action.payload, 'campaigns', []),
      };
    }
    default:
      return state;
  }
};

export default newtrident;
